import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import HowCitation from './HowCitation';

const HOWIE_BLOCK_RE = /:::(how-citation|sacred-quote|dojo-[\w-]+)\b([^\n]*)\n?([\s\S]*?):::/g;
const HOWIE_BLOCK_OPEN_RE = /:::(how-citation|sacred-quote|dojo-[\w-]+)\b/g;
const DOJO_BLOCK_RE = /:::dojo-[\w-]+\b[\s\S]*?:::/g;
const INSIGHT_PREVIEW_BLOCK_RE = /:::(?:chiddush-preview|insight-preview)\b[^\n]*\n?([\s\S]*?):::/g;
const LEADING_RULE_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*/;

interface CitationData {
  quote: string;
  attribution?: string;
  videoRef?: string;
}

function stripOuterQuotes(value: string): string {
  return value
    .trim()
    .replace(/^["'\u201c\u201d]+/, '')
    .replace(/["'\u201c\u201d]+$/, '')
    .trim();
}

export function stripPartialHowieBlock(content: string): string {
  let lastOpen: RegExpExecArray | null = null;
  let match: RegExpExecArray | null;
  const openRe = new RegExp(HOWIE_BLOCK_OPEN_RE);

  while ((match = openRe.exec(content)) !== null) {
    lastOpen = match;
  }

  if (!lastOpen) return content;

  const closeIdx = content.indexOf(':::', lastOpen.index + lastOpen[0].length);
  return closeIdx === -1 ? content.slice(0, lastOpen.index) : content;
}

export function prepareHowieMarkdown(content: string): string {
  return stripPartialHowieBlock(content)
    .replace(INSIGHT_PREVIEW_BLOCK_RE, '$1')
    .replace(DOJO_BLOCK_RE, '')
    .replace(LEADING_RULE_RE, '')
    .trim();
}

function parseCitation(rawOpening: string, rawBody: string, defaultAttribution?: string): CitationData {
  const opening = rawOpening.trim();
  const body = rawBody.trim();
  const joined = [opening, body].filter(Boolean).join('\n').trim();
  const lines = joined.split('\n').map((line) => line.trim()).filter(Boolean);
  const videoRef = lines.find((line) => line.startsWith('ref:') || line.startsWith('Ref:'))
    ?.replace(/^ref:\s*/i, '');

  let metadata: Record<string, unknown> | null = null;
  const jsonStart = joined.lastIndexOf('{');
  const jsonEnd = joined.lastIndexOf('}');
  if (jsonStart !== -1 && jsonEnd > jsonStart) {
    try {
      metadata = JSON.parse(joined.slice(jsonStart, jsonEnd + 1));
    } catch {
      metadata = null;
    }
  }

  const attributionLineIndex = lines.findIndex((line) => /^[-\u2013\u2014]\s*/.test(line));
  const attribution = typeof metadata?.author === 'string'
    ? metadata.author
    : attributionLineIndex !== -1
    ? lines[attributionLineIndex].replace(/^[-\u2013\u2014]\s*/, '').trim()
    : defaultAttribution;

  const source = typeof metadata?.book === 'string'
    ? metadata.book
    : typeof metadata?.source === 'string'
    ? metadata.source
    : undefined;

  let quoteSource = joined;
  if (metadata && jsonStart !== -1 && jsonEnd > jsonStart) {
    quoteSource = `${joined.slice(0, jsonStart)}${joined.slice(jsonEnd + 1)}`.trim();
  }
  if (attributionLineIndex !== -1) {
    quoteSource = lines.slice(0, attributionLineIndex).join('\n');
  }
  if (videoRef) {
    quoteSource = quoteSource.replace(/^ref:\s*.+$/im, '').trim();
  }

  const quote = stripOuterQuotes(quoteSource);
  const attributionWithSource = attribution && source ? `${attribution}, ${source}` : attribution;

  return {
    quote,
    attribution: attributionWithSource,
    videoRef,
  };
}

function renderMarkdownText(content: string, key?: string): ReactNode {
  const prepared = prepareHowieMarkdown(content);
  if (!prepared) return null;

  return (
    <ReactMarkdown key={key} remarkPlugins={[remarkGfm]}>
      {prepared}
    </ReactMarkdown>
  );
}

export function renderHowieMarkdown(content: string): ReactNode {
  const prepared = prepareHowieMarkdown(content);
  if (!prepared) return null;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const blockRe = new RegExp(HOWIE_BLOCK_RE);

  while ((match = blockRe.exec(prepared)) !== null) {
    if (match.index > lastIndex) {
      parts.push(renderMarkdownText(prepared.slice(lastIndex, match.index), `text-${lastIndex}`));
    }

    const [, tagName, opening, body] = match;
    if (tagName === 'how-citation' || tagName === 'sacred-quote') {
      const citation = parseCitation(
        opening,
        body,
        tagName === 'sacred-quote' ? 'Dov Seidman' : undefined
      );

      if (citation.quote) {
        parts.push(
          <HowCitation
            key={`${tagName}-${match.index}`}
            quote={citation.quote}
            attribution={citation.attribution}
            videoRef={citation.videoRef}
          />
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < prepared.length) {
    parts.push(renderMarkdownText(prepared.slice(lastIndex), `text-${lastIndex}`));
  }

  const visibleParts = parts.filter(Boolean);
  return visibleParts.length === 1 ? visibleParts[0] : visibleParts;
}
